/**
 * Login route — member sign-in entry shell (post D-060 client rescission).
 *
 * The live member sign-in entry is the SERVER `/login` route (the Telia
 * start, `GET /login`, 032 US1): `RouteGuard` sends an anonymous visitor
 * there via a FULL-PAGE redirect (`window.location.assign`), NOT a
 * react-router `<Navigate>`. This SPA `/login` descriptor stays registered
 * (`shell/routes.ts`) — it is the one public render descriptor and the shell
 * chrome (`Layout`/`TopBar`) keys its `/login` presentation off the path — so
 * this component renders a minimal, demo-free sign-in shell: the heading plus
 * the honest-demo disclaimer.
 *
 * The D-060 client demo machinery was rescinded in T035: the in-app email +
 * password credential form, the `useDemoSessionControls`/`setActiveDemoMember`
 * fixture discriminator, the `login-form.ts` static credential table, and the
 * "Demo accounts" enumeration panel are all gone. Production members never
 * reach this SPA surface to sign in (the guard's full-page nav hits the api
 * before the SPA route would mount); when an Entra/Telia-backed member SPA
 * sign-in surface is needed it replaces this shell.
 *
 * Both rendered strings are i18n keys retained in the member bundle:
 *   - `login.heading` — the neutral sign-in heading.
 *   - `login.disclaimer.demoIntent` — the canonical demo-disclosure surface
 *     (D-PD-003, D-050), kept in place as the honest-demo posture.
 */

import type { JSX } from 'react';

import { useTranslate } from '../i18n/useTranslate.js';
import { Card } from '../primitives/Card.js';

// ---------- Styles ----------

const pageClasses = 'flex flex-col gap-4';
const headingClasses = 'm-0 text-xl font-semibold';
const disclaimerClasses = 'm-0 text-sm text-muted-foreground';

export function Login(): JSX.Element {
  const t = useTranslate();

  return (
    <Card className={pageClasses}>
      <h2 className={headingClasses}>{t('login.heading')}</h2>
      <p className={disclaimerClasses}>{t('login.disclaimer.demoIntent')}</p>
    </Card>
  );
}
